import * as React from "react";
import { format, parse } from "date-fns";
import { cs } from "date-fns/locale";
import { Calendar as CalendarIcon, X as ClearIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import { Button } from "@/ui/primitives/button";
import { Calendar } from "@/ui/primitives/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/ui/primitives/popover";
import { TimePicker } from "@/ui/primitives/time-picker";

type Props = {
  /** datetime-local string `"yyyy-MM-ddTHH:mm"` or empty. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  id?: string;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
  ariaLabel?: string;
};

const FMT = "yyyy-MM-dd'T'HH:mm";

function stringToParts(value: string): { date: Date | undefined; timeStr: string } {
  if (!value) return { date: undefined, timeStr: "" };
  const parsed = parse(value, FMT, new Date());
  if (Number.isNaN(parsed.getTime())) return { date: undefined, timeStr: "" };
  return { date: parsed, timeStr: format(parsed, "HH:mm") };
}

function compose(date: Date | undefined, time: string): string {
  if (!date) return "";
  const [hh = "00", mm = "00"] = (time || "00:00").split(":");
  const out = new Date(date);
  out.setHours(parseInt(hh, 10) || 0, parseInt(mm, 10) || 0, 0, 0);
  return format(out, FMT);
}

/**
 * LGo datetime picker — hybrid:
 *  - shadcn Popover + Calendar (radix-based, react-day-picker) for date.
 *    Works inside other radix Dialogs because they share the same
 *    portal/focus-trap engine.
 *  - react-aria-components TimeField (inline only, no popover) for time
 *    segments — solid keyboard handling without a vendor CSS file.
 *
 * Bun-friendly: no vendor CSS imports anywhere.
 * Emits the same `"yyyy-MM-ddTHH:mm"` string format as the native
 * `<input type="datetime-local">`, so existing form schemas work unchanged.
 */
export const DateTimePicker = React.forwardRef<HTMLButtonElement, Props>(function DateTimePicker(
  { value, onChange, disabled, id, className, minDate, maxDate, ariaLabel },
  ref,
) {
  const { date, timeStr } = stringToParts(value);
  const [open, setOpen] = React.useState(false);

  const handleDateSelect = (next: Date | undefined) => {
    if (next) onChange(compose(next, timeStr || "00:00"));
    setOpen(false);
  };

  const handleTimeChange = (next: string) => {
    onChange(compose(date, next));
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
  };

  const datePretty = date ? format(date, "d. MMMM yyyy", { locale: cs }) : "";

  return (
    <div className={cn("flex items-stretch gap-2", className)}>
      {/* Date trigger + popover */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            type="button"
            variant="outline"
            id={id}
            disabled={disabled}
            className={cn(
              "flex-1 justify-start text-left font-normal h-10 min-w-0",
              !date && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="h-4 w-4 mr-2 shrink-0" />
            <span className="flex-1 truncate tabular-nums">
              {datePretty || "Vyberte datum"}
            </span>
            {date && !disabled && (
              <span
                role="button"
                tabIndex={-1}
                onClick={handleClear}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange("");
                  }
                }}
                className="ml-2 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                aria-label="Vymazat"
              >
                <ClearIcon className="h-3.5 w-3.5" />
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleDateSelect}
            disabled={(d) => {
              if (minDate && d < minDate) return true;
              if (maxDate && d > maxDate) return true;
              return false;
            }}
            locale={cs}
            weekStartsOn={1}
            initialFocus
          />
        </PopoverContent>
      </Popover>

      {/* Inline time field — same component as the standalone time picker. */}
      <TimePicker
        ariaLabel={ariaLabel}
        value={timeStr}
        onChange={handleTimeChange}
        disabled={disabled || !date}
        className="w-32 shrink-0"
      />
    </div>
  );
});
